import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import styled from "styled-components";
import { request } from "hooks/useOkoApi/useOkoApi";
import { normalizeGroupAssignments } from "hooks/useCurriculum/useCurriculum";
import { colors, logEducatorDashboard, LogLevel } from "utils";
import { CustomizedTag } from "components/shared/CustomizedTag/CustomizedTag";
import "./dashboard.css";

const StandardBox = styled.div`
  align-items: center;
  background-color: ${colors.blue50};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  width: 100%;
`;

const StandardRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  width: 100%;
`;

const LevelTag = styled.span`
  background-color: ${colors.green50};
  border: 1px solid #76d9ad;
  border-radius: 11px;
  color: ${colors.green700};
  font-size: 14px;
  line-height: 20px;
  padding: 2px 8px;
  white-space: nowrap;
`;


const StandardText = styled.span`
  color: #002e19;
  font-size: 18px;
  letter-spacing: 0.25px;
  line-height: 26px;
`;

const StandardName = styled(StandardText)`
  width: 100%;
`;

const TRACKS = [
  { key: "core", label: "Core" },
  { key: "prerequisites", label: "Prerequisites" },
  { key: "extensions", label: "Extensions" },
];

const PRIMARY_BTN_STYLE = (active) => ({
  alignItems: "center",
  backgroundColor: active ? "#605DEC" : "#D5D3CE",
  border: "none",
  borderRadius: 8,
  color: "#FAF7F2",
  display: "inline-flex",
  fontFamily: "Inter, sans-serif",
  fontSize: 18,
  fontWeight: 700,
  height: 48,
  justifyContent: "center",
  letterSpacing: "0.25px",
  lineHeight: "26px",
  padding: "0 16px",
  width: 180,
});

const SECONDARY_BTN_STYLE = {
  alignItems: "center",
  backgroundColor: "white",
  border: "1px solid #605DEC",
  borderRadius: 8,
  color: "#605DEC",
  display: "inline-flex",
  fontFamily: "Inter, sans-serif",
  fontSize: 18,
  fontWeight: 700,
  height: 48,
  justifyContent: "center",
  letterSpacing: "0.25px",
  lineHeight: "26px",
  padding: "0 16px",
  width: 180,
};

const Checkbox = ({ checked, onChange, onDark = false, ariaLabel }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-checked={checked}
    aria-label={ariaLabel}
    role="checkbox"
    style={{
      alignItems: "center",
      background: checked ? "#0DA662" : "transparent",
      border: `2px solid ${checked ? "#0DA662" : (onDark ? "white" : "#D3D3F8")}`,
      borderRadius: 4,
      cursor: "pointer",
      display: "flex",
      height: 24,
      justifyContent: "center",
      padding: 0,
      width: 24,
    }}
  >
    {checked && (
      <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 5L4.5 8.5L12 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </button>
);

export const AssignGroupsDistributionModal = ({
  isOpen,
  onClose,
  classroomId,
  standardCode,
  standardName,
  totalAvailable,
  hasCore = true,
  hasPrerequisites = true,
  hasExtensions = true,
  // Track keys (core / prerequisites / extensions) that exist but have no questions
  // to assign — shown but disabled (count locked at 0), so groups can't be sent to a
  // level with nothing to work on.
  noQuestionTracks = [],
  // Track keys (core / prerequisites / extensions) whose question set the teacher has
  // filtered — shown with a "Customized" tag so they know a filter will be applied.
  customizedTracks = [],
  initialCounts,
  onSaved,
  onCleared,
  onSubmit,
  submitLabel = "Save",
  // Lesson/curriculum context: source the clearable pool from the unified
  // group-assignments read and route each cleared code's unassign by its owning
  // bundle_type. Defaults false so the differentiation callers (ViewChangeStandard,
  // StandardDetailView, standard-mode LessonsStandards) keep the groups/-only path.
  useUnifiedPool = false,
  // Optional lesson-assign variant: when provided, the info box shows the
  // lesson context ("Grade X, unit Y, Lesson Z" + lesson name) instead of the
  // "Core" standard tag/code.
  infoSubtitle,
  infoTitle,
  // Advanced Selection: TrackQuestionFilter[] attached to the fallback
  // POST /groups/assign path. Ignored when onSubmit is supplied — that caller threads
  // filters through its own submit function instead (see ViewChangeLesson/ViewChangeStandard).
  questionFilters,
}) => {
  // A track with no questions is disabled; force its count to 0.
  const trackDisabled = (key) => noQuestionTracks.includes(key);
  const trackCustomized = (key) => customizedTracks.includes(key);

  // When a track isn't available on the new standard (or has no questions), force
  // its count to 0 even if initialCounts carried a value over from a prior standard.
  const seedCounts = (init) => ({
    core: hasCore && !trackDisabled("core") ? (init?.core || 0) : 0,
    prerequisites: hasPrerequisites && !trackDisabled("prerequisites") ? (init?.prerequisites || 0) : 0,
    extensions: hasExtensions && !trackDisabled("extensions") ? (init?.extensions || 0) : 0,
  });

  const [counts, setCounts] = useState(seedCounts(initialCounts));
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState(null);

  // "distribute" — main modal; "clear" — sub-modal for clearing already-assigned codes.
  const [view, setView] = useState("distribute");
  // Working selection inside the clear sub-modal.
  const [subSelection, setSubSelection] = useState(() => new Set());
  // All codes currently assigned somewhere in the classroom — candidates the educator could clear.
  const [clearable, setClearable] = useState([]);
  // Codes the educator has released to the pool during this open. Bumps the available count
  // optimistically until the parent re-fetches and props update.
  const [clearedCount, setClearedCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setCounts(seedCounts(initialCounts));
    setError(null);
    setView("distribute");
    setSubSelection(new Set());
    setClearedCount(0);

    if (!classroomId) return;
    let cancelled = false;
    (async () => {
      try {
        // Unified path: read all bundles (lesson + standard) so the clear step
        // lists every assigned code; the unassign endpoint resolves ownership.
        if (useUnifiedPool) {
          const raw = await request(`curriculum/classrooms/${classroomId}/group-assignments`, { method: "GET" });
          if (cancelled) return;
          const data = normalizeGroupAssignments(raw);
          const flat = [];
          data.lessons.forEach((b) => {
            (b.assignments || []).forEach((a) => {
              if (a.group_code && a.track_type && a.track_type !== "no_assignment") {
                flat.push({
                  group_code: a.group_code,
                  assignment_id: b.assignment_id,
                  standard_code: a.standard_code,
                  standard_name: b.bundle_type === "lesson"
                    ? (b.lesson?.name || a.standard_code || "")
                    : (b.standard?.standard_name || a.standard_code || ""),
                });
              }
            });
          });
          flat.sort((a, b) => a.group_code.localeCompare(b.group_code));
          setClearable(flat);
          return;
        }

        const wrapper = await request(`groups/classroom/${classroomId}/assignment`, { method: "GET" });
        if (cancelled) return;
        const flat = [];
        (wrapper?.assignments || []).forEach((bundle) => {
          (bundle.assignments || []).forEach((a) => {
            if (a.standard_code && a.group_code) {
              flat.push({
                group_code: a.group_code,
                assignment_id: bundle.assignment_id,
                standard_code: a.standard_code,
                standard_name: a.standard_name || bundle.primary_standard_name || "",
              });
            }
          });
        });
        flat.sort((a, b) => a.group_code.localeCompare(b.group_code));
        setClearable(flat);
      } catch (e) {
        logEducatorDashboard("AssignGroupsDistributionModal: failed to load classroom assignments", {
          level: LogLevel.WARNING,
          event: "assign_load_failed",
          statusCode: e?.status,
        });
      }
    })();
    return () => { cancelled = true; };
  // initialCounts intentionally omitted — only re-seed on open / classroom / standard change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, classroomId, standardCode, useUnifiedPool]);

  const visibleTracks = useMemo(
    () => TRACKS.filter(({ key }) =>
      (key === "core" && hasCore) ||
      (key === "prerequisites" && hasPrerequisites) ||
      (key === "extensions" && hasExtensions)
    ),
    [hasCore, hasPrerequisites, hasExtensions]
  );

  const effectiveTotalAvailable = totalAvailable + clearedCount;
  const total = useMemo(() => counts.core + counts.prerequisites + counts.extensions, [counts]);
  const atMax = total >= effectiveTotalAvailable;
  const canSave = total > 0 && !saving;
  const hasClearable = clearable.length > 0;

  const bump = (key, delta) => {
    if (delta > 0 && trackDisabled(key)) return; // can't add groups to a question-less track
    setCounts((prev) => {
      const next = { ...prev, [key]: Math.max(0, prev[key] + delta) };
      const nextTotal = next.core + next.prerequisites + next.extensions;
      if (nextTotal > effectiveTotalAvailable) return prev;
      return next;
    });
  };

  const openClearView = () => {
    setSubSelection(new Set());
    setView("clear");
  };

  const toggleSubRow = (groupCode) => {
    setSubSelection((prev) => {
      const next = new Set(prev);
      if (next.has(groupCode)) next.delete(groupCode);
      else next.add(groupCode);
      return next;
    });
  };

  const toggleSubAll = () => {
    setSubSelection((prev) => {
      if (prev.size === clearable.length) return new Set();
      return new Set(clearable.map((c) => c.group_code));
    });
  };

  const commitSubSelection = async () => {
    if (subSelection.size === 0 || clearing || !classroomId) return;
    setClearing(true);
    setError(null);
    try {
      if (useUnifiedPool) {
        // Dedicated batch unassign: the backend resolves each code's owning bundle
        // (lesson or standard) and releases it to the pool in one atomic call.
        const codes = clearable.filter((c) => subSelection.has(c.group_code)).map((c) => c.group_code);
        await request(`curriculum/classrooms/${classroomId}/group-assignments/unassign`, {
          method: "POST",
          payload: { group_codes: codes },
        });
      } else {
        const byBundle = new Map();
        clearable.forEach((c) => {
          if (!subSelection.has(c.group_code)) return;
          const list = byBundle.get(c.assignment_id) || [];
          list.push({ group_code: c.group_code, track_type: null, standard_code: null });
          byBundle.set(c.assignment_id, list);
        });
        await Promise.all(
          Array.from(byBundle.entries()).map(([assignmentId, updates]) =>
            request(
              `groups/classrooms/${classroomId}/assignments/${assignmentId}`,
              { method: "PUT", payload: { assignments: updates } },
            )
          )
        );
      }
      const freed = subSelection.size;
      setClearedCount((prev) => prev + freed);
      setClearable((prev) => prev.filter((c) => !subSelection.has(c.group_code)));
      setSubSelection(new Set());
      setView("distribute");
      onCleared?.(freed);
    } catch (e) {
      logEducatorDashboard("AssignGroupsDistributionModal: clear failed", {
        level: LogLevel.WARNING,
        event: "clear_failed",
        statusCode: e?.status,
      });
      setError(e?.detail || e?.message || "Failed to clear group codes. Try again.");
    } finally {
      setClearing(false);
    }
  };

  const onSave = async () => {
    // The fallback POST keys off standardCode, but the onSubmit flow (lesson
    // assign / edit) identifies its target itself — so only require a
    // standardCode when there is no onSubmit. This lets core-less lessons
    // (no addressing standard) still assign prereq/extension groups.
    if (!canSave || !classroomId || (!onSubmit && !standardCode)) return;
    setSaving(true);
    setError(null);
    try {
      // Edit flow supplies onSubmit (commits via PUT on the existing bundle);
      // the new-assignment flow falls back to the pool-gated POST /groups/assign.
      if (onSubmit) {
        await onSubmit(counts);
      } else {
        await request("groups/assign", {
          method: "POST",
          payload: {
            classroom_id: classroomId,
            standard_code: standardCode,
            distribution: counts,
            ...(questionFilters?.length ? { question_filters: questionFilters } : {}),
          },
        });
      }
      onSaved?.();
    } catch (e) {
      logEducatorDashboard("AssignGroupsDistributionModal: save failed", {
        level: LogLevel.WARNING,
        event: "assign_failed",
        statusCode: e?.status,
      });
      setError(e?.detail || e?.message || "Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSubChecked = clearable.length > 0 && subSelection.size === clearable.length;

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" contentClassName="agdm-modal-content" aria-labelledby="agdm-title">
      <Modal.Header style={{ alignItems: "center", border: "none", display: "flex", justifyContent: "space-between", marginBottom: 24, padding: 0, width: "100%" }}>
        <Modal.Title id="agdm-title" style={{ color: "#002E19", fontSize: 24, fontWeight: 600, lineHeight: "32px" }}>
          {view === "clear" ? "Clear Group Codes to Reassign" : "Assign Groups"}
        </Modal.Title>
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            alignItems: "center",
            background: "transparent",
            border: "2px solid #00140B",
            borderRadius: "100px",
            cursor: "pointer",
            display: "flex",
            height: 48,
            justifyContent: "center",
            padding: 0,
            width: 48,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6 6 18M6 6l12 12" stroke="#00140B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </Modal.Header>

      {view === "distribute" ? (
        <>
          <Modal.Body style={{ padding: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <StandardBox style={infoSubtitle ? { alignItems: "flex-start", gap: 4 } : undefined}>
                {infoSubtitle ? (
                  <>
                    <StandardText as="p" style={{ fontWeight: 400, margin: 0 }}>{infoSubtitle}</StandardText>
                    <StandardName as="p" style={{ fontWeight: 700, margin: 0 }}>{infoTitle}</StandardName>
                  </>
                ) : (
                  <>
                    <StandardRow>
                      <LevelTag>Core</LevelTag>
                      <StandardText>{standardCode}</StandardText>
                    </StandardRow>
                    <StandardName>{standardName}</StandardName>
                  </>
                )}
              </StandardBox>
              <div style={{ color: "#002E19", display: "flex", flexDirection: "column", fontSize: 18, fontWeight: 400, gap: 8, letterSpacing: "0.25px", lineHeight: "26px" }}>
                <span>Select the number of groups to assign to each level.</span>
                <span>
                  Don&apos;t worry, once your groups are created, you can click &quot;<strong style={{ fontWeight: 700 }}>Edit</strong>&quot; to
                  choose specific prerequisite and extension standards, and customize the question set for each group.
                </span>
              </div>
              <div style={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 24, padding: "0 32px" }}>
                {visibleTracks.map(({ key, label }) => (
                  <div key={key} style={{ alignItems: "center", display: "flex", gap: 24 }}>
                    <div style={{ alignItems: "flex-start", color: trackDisabled(key) ? colors.milkGlass800 : "#002E19", display: "flex", flexDirection: "column", fontSize: 16, gap: 4, letterSpacing: "0.25px", lineHeight: "22px", width: 115 }}>
                      <span>{label}</span>
                      {trackCustomized(key) && <CustomizedTag />}
                    </div>
                    <div style={{
                      alignItems: "center",
                      backgroundColor: "white",
                      border: "1px solid #BAB9F5",
                      borderRadius: 6,
                      display: "flex",
                      height: 48,
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      width: 132,
                    }}>
                      <span style={{ color: "#002E19", fontSize: 16, letterSpacing: "0.25px", lineHeight: "22px" }}>{counts[key]}</span>
                      <div style={{ alignItems: "center", display: "flex", gap: 16 }}>
                        <button
                          aria-label={`Increase ${label}`}
                          disabled={atMax || trackDisabled(key)}
                          onClick={() => bump(key, +1)}
                          style={{
                            alignItems: "center",
                            background: "transparent",
                            border: "none",
                            color: atMax || trackDisabled(key) ? colors.milkGlass800 : colors.blue500,
                            cursor: atMax || trackDisabled(key) ? "not-allowed" : "pointer",
                            display: "flex",
                            padding: 0,
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <line x1="12" y1="5" x2="12" y2="19" />
                          </svg>
                        </button>
                        <button
                          aria-label={`Decrease ${label}`}
                          disabled={counts[key] === 0}
                          onClick={() => bump(key, -1)}
                          style={{
                            alignItems: "center",
                            background: "transparent",
                            border: "none",
                            color: counts[key] === 0 ? colors.milkGlass800 : colors.blue500,
                            cursor: counts[key] === 0 ? "not-allowed" : "pointer",
                            display: "flex",
                            padding: 0,
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ alignItems: "baseline", color: "#002E19", display: "flex", fontSize: 18, gap: 8, letterSpacing: "0.25px", lineHeight: "26px" }}>
                <span>{total} of {effectiveTotalAvailable} Groups Selected</span>
                {hasClearable && (
                  <button
                    type="button"
                    onClick={openClearView}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#605DEC",
                      cursor: "pointer",
                      fontSize: 18,
                      letterSpacing: "0.25px",
                      lineHeight: "26px",
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Clear More Group Codes
                  </button>
                )}
              </div>
              {error && (
                <div role="alert" style={{ color: "crimson", fontSize: 14 }}>{error}</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={{ border: "none", padding: 0, marginTop: 8, gap: 24, justifyContent: "flex-end" }}>
            <Button onClick={onClose} style={SECONDARY_BTN_STYLE}>Cancel</Button>
            <Button disabled={!canSave} onClick={onSave} aria-busy={saving} style={PRIMARY_BTN_STYLE(canSave)}>
              {saving ? "Saving…" : submitLabel}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body style={{ padding: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ color: "black", fontSize: 18, fontWeight: 400, letterSpacing: "0.25px", lineHeight: "26px" }}>
                Select the group codes you want to clear so they can be reassigned for the new session.
              </div>
              <div role="table" aria-label="Assigned group codes" style={{ display: "flex", flexDirection: "column" }}>
                <div role="row" style={{ alignItems: "stretch", backgroundColor: "#002E19", display: "flex" }}>
                  <div role="columnheader" style={{ alignItems: "center", display: "flex", padding: "8px 16px" }}>
                    <Checkbox checked={allSubChecked} onChange={toggleSubAll} onDark ariaLabel="Select all group codes" />
                  </div>
                  <div role="columnheader" style={{ alignItems: "center", color: "white", display: "flex", fontSize: 16, fontWeight: 700, justifyContent: "center", letterSpacing: "0.25px", lineHeight: "22px", padding: "8px 16px", width: 128 }}>
                    Group Code
                  </div>
                  <div role="columnheader" style={{ alignItems: "center", color: "white", display: "flex", flex: "1 0 0", fontSize: 16, fontWeight: 700, letterSpacing: "0.25px", lineHeight: "22px", minWidth: 0, padding: "8px 16px" }}>
                    Assigned to
                  </div>
                </div>
                {clearable.map((row) => {
                  const checked = subSelection.has(row.group_code);
                  return (
                    <div role="row" key={row.group_code} style={{ alignItems: "stretch", borderBottom: "1px solid #8D8D8D", display: "flex", height: 54 }}>
                      <div role="cell" style={{ alignItems: "center", display: "flex", padding: "8px 16px" }}>
                        <Checkbox checked={checked} onChange={() => toggleSubRow(row.group_code)} ariaLabel={`Select group code ${row.group_code}`} />
                      </div>
                      <div role="cell" style={{ alignItems: "center", color: "#002E19", display: "flex", fontSize: 16, letterSpacing: "0.25px", lineHeight: "22px", padding: "8px 16px", width: 128 }}>
                        {row.group_code}
                      </div>
                      <div role="cell" style={{ alignItems: "center", color: "#002E19", display: "flex", flex: "1 0 0", fontSize: 14, lineHeight: "20px", minWidth: 0, padding: "8px 16px" }}>
                        {row.standard_code}{row.standard_name ? `: ${row.standard_name}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ border: "none", padding: 0, marginTop: 8, justifyContent: "space-between" }}>
            <Button onClick={() => setView("distribute")} style={SECONDARY_BTN_STYLE}>Back</Button>
            <Button
              disabled={subSelection.size === 0 || clearing}
              onClick={commitSubSelection}
              aria-busy={clearing}
              style={{ ...PRIMARY_BTN_STYLE(subSelection.size > 0 && !clearing), width: "auto", minWidth: 180 }}
            >
              {clearing
                ? "Clearing…"
                : subSelection.size === 0
                  ? "Clear Group Codes"
                  : `Clear ${subSelection.size} Group Code${subSelection.size === 1 ? "" : "s"}`}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
